import {
  action,
  computed,
  configure,
  decorate,
  observable,
} from 'mobx';
import {
  getStoredAuctionIdByPath,
  getStoredAuctionLaneIdByPath,
  setStoredAuctionIdByPath,
  setStoredAuctionLaneIdByPath,
} from 'shared/utils/localStorage';
import mockdata from '../__mocks__/data/product_scratch.json';
import {
  getDataIndexByPropName,
  getListDataById,
  hasData,
  mapToObj,
} from './utils';

configure({ enforceActions: 'always' });

class AuctionStore {
  constructor() {
    this.initialize();
  }

  setProperty = (key, value) => {
    this[key] = value;
  };

  getProperty = (key) => this[key];

  initialize = () => {
    this.setProperty('settings', mockdata.settings);
    this.initializeAuction();
  };
/*
{"type":1,"target":"LaneClients","arguments":["8b5d8399-54b7-eb11-a86d-f4939feae20c",{"laneClients":[{"account":"100035","accountName":"VERTX","member":"MOTOWOOD2","memberName":"MOTOWOOD2 (100035)","bidderNumber":"100035","enableBuyer":true,"enableSeller":true,"viewOnly":false}]}]}
*/
  initializeAuction = () => {
    this.setProperty('laneMediaMuted', false);
    this.setProperty('auctionList', []);
    this.setProperty('auctionID', getStoredAuctionIdByPath());
    this.setProperty('laneList', []);
    this.setProperty('laneClientList', []);
    this.setProperty('laneStatus', null);
    this.setProperty('auctionName', null);
    this.setProperty('connecting', false);
    this.setProperty('networkConnected', true);
    this.setProperty('auctionSettings', []);
    this.setProperty('auctionInfo', {});
    this.setProperty('layoutSettings', []);
    this.setProperty('laneEvents', []);
    this.setProperty('onClerkScreen', false);
    this.initializeLane();
  };

    initializeLane = () => {
      this.setProperty('laneList', []);
      this.setProperty('laneMetrics', null);
      this.setProperty('laneSettings', []);
      this.setProperty('laneMediaSettings', []);
      this.setProperty('auctionLaneName', null);
      this.setProperty('auctionLaneId', null);
      this.setProperty('lastAuctionLaneId', null);
      this.initializeProducts();
    };

  initializeProducts = () => {
    this.setProperty('runList', []);
    this.setProperty('runListStats', null);
    this.setProperty('runListFiltered', []);
  };

  get getAuctionLaneIdForInvocation() {
    return (
      getStoredAuctionLaneIdByPath() ?? this.auctionStore?.lastAuctionLaneId
    );
  }

  setAuctionInfo = (data) => {
    this.setProperty('auctionInfo', data);
  };

  setLaneMediaSettings = (data) => this.setProperty('laneMediaSettings', data);

  setAuctionSettings = (data) => this.setProperty('auctionSettings', data);

  setLayoutSettings = (data) => this.setProperty('layoutSettings', data);

  getLayoutSettingByProp = (prop, value) => this.layoutSettings?.filter((item) => item[prop] === value)[0];

  setLaneSettings = (data) => this.setProperty('laneSettings', data);

  getLaneSettingByProp = (prop) => this.laneSettings?.filter((item) => item.laneSetting === prop)[0]?.value;

  getLaneSettingArrayByProp = (prop) => this.laneSettings?.filter((item) => item.laneSetting === prop)[0]?.valueAsArray;

  setLaneSetting = (prop, value) => {
    this.laneSettings.filter((item) => item.laneSetting === prop)[0].value = value;
  }

  toggleLaneMedia = (value) => {
    this.setProperty('laneMediaMuted', value !== 'Running');
  };

  setAuctionList = (data) => {
    const noAuctionData = !this.auctionList.length;
    if (noAuctionData) this.auctionList = data;
  };

  setLaneList = (data) => {
    const noLaneData = !this.laneList.length;
    if (noLaneData) this.laneList = data;
  };

  setRunList = (data, productStates = [0, 1, 2, 3, 4]) => {
    this.setProperty('runList', data);
    this.setProperty('runListFiltered', data);
    const stats = new Map();
    productStates.map((state) => stats.set(state, getListDataById(data, 'productState', state).length));
    this.setProperty('runListStats', mapToObj(stats));
  };

  setFilteredRunList = (data) => {
    if (!data) this.setProperty('runListFiltered', this.runList);
    this.setProperty('runListFiltered', data);
  };

  // TEMP: should get an updated product from the server instead
  updateRunListItemState = (lotNumber, productState) => {
    const item = this.runList.find((runListItem) => runListItem.lotNumber === lotNumber);

    const getProductState = () => {
      if (productState === 'NoSale') return 4;
      if (productState === 'Sold') return 3;
      if (productState === 'SubjectTo') return 2;
      if (productState === 'Open') return 1;
      if (productState === 'None') return 0;
      return 0;
    };

    item.productState = getProductState();
    this.setRunList(Object.assign([...this.runList], item));
  };

  getRunListProductByIndex = (index) => this.runList[index];

  setRunListProductByIndex = (index, data) => {
    this.runList[index] = data;
  };

  updateRunListWithPropValue = (lotNumber, propName, value) => {
    const index = getDataIndexByPropName(this.runList, 'lotNumber', lotNumber);
    const product = this.getRunListProductByIndex(index);
    product[propName] = value;
    this.setRunListProductByIndex(index, product);
  };

  getAuctionNameById = (data, id) => data.filter((item) => item.auctionID === id).auction;

  getLaneNameById = (data, id) => data.filter((item) => item.laneID === id).lane;

  setAuction = (id) => {
    setStoredAuctionIdByPath(id);
    const auctionData = getListDataById(this.auctionList, 'auctionID', id);
    this.setProperty('auctionID', id);
    this.setProperty('auctionName', auctionData[0]?.auction);
  };

  setLane = (id) => {
    setStoredAuctionLaneIdByPath(id);
    const laneData = getListDataById(this.laneList, 'laneID', id);
    this.setProperty('auctionLaneId', id);
    this.setProperty('lastAuctionLaneId', id);
    this.setProperty('auctionLaneName', laneData?.length ? laneData[0].lane : '');
  };

  setLaneMetrics = (data) => {
    this.setProperty('laneMetrics', data);
  };

  get getAuctionAndLaneTitle() {
    const title = [];
    const auction = this.auctionName;
    const lane = this.auctionLaneName;
    if (hasData(auction)) title.push(auction);
    if (hasData(lane)) title.push(`, ${lane}`);
    return title.join('');
  }
}

decorate(AuctionStore, {
  auctionID: observable,
  auctionInfo: observable,
  auctionLaneId: observable,
  auctionLaneName: observable,
  auctionList: observable,
  auctionName: observable,
  connected: observable,
  getAuctionAndLaneTitle: computed,
  getAuctionLaneIdForInvocation: computed,
  getLaneSettingArrayByProp: action,
  getLaneSettingByProp: action,
  getLayoutSettingByProp: action,
  getListDataById: action,
  getProperty: action,
  initialize: action,
  initializeAuction: action,
  initializeLane: action,
  initializeProducts: action,
  laneClientList: observable,
  laneList: observable,
  laneMediaMuted: observable,
  laneMetrics: observable,
  laneSettings: observable,
  laneMediaSettings: observable,
  laneStatus: observable,
  lastAuctionLaneId: observable,
  layoutSettings: observable,
  networkConnected: observable,
  onClerkScreen: observable,
  runList: observable,
  runListFiltered: observable,
  runListStats: observable,
  setAuction: action,
  setAuctionList: action,
  setAuctionSettings: action,
  setLaneMediaSettings: action,
  setFilteredRunList: action,
  setLane: action,
  setLaneList: action,
  setLaneMetrics: action,
  setLaneSetting: action,
  setLaneSettings: action,
  setLayoutSettings: action,
  setProperty: action,
  setRunList: action,
  settings: observable,
  toggleLaneMedia: action,
  updateRunListItemState: action,
  updateRunListWithPropValue: action,
});

export default AuctionStore;
